"use client";

// imports
import Link from 'next/link';
import { Scan, ArrowRight, CheckCircle, ScanHeart } from 'lucide-react';
import { useInView } from '@/hooks/useInView';

// constants
const benefits = [
  'No account required',
  'Results delivered in seconds',
  'Your image is never shared',
  'Free to use — always',
];

// types
interface CTASectionProps {
  sectionBg?: string;
}

// component
export default function CTASection({sectionBg = "bg-gradient-to-br from-slate-50 to-blue-50" }: CTASectionProps) {
  
  // hooks
  const { ref, isVisible } = useInView<HTMLDivElement>({
    threshold: 0.2,
    once: true,
  });

  return (
    <section ref={ref} className={`py-20 lg:py-28 ${sectionBg}`}>
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-16">
        <div
          className={`
            relative bg-gradient-to-br from-blue-600 via-blue-600 to-cyan-600 rounded-3xl overflow-hidden shadow-strong

            transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]

            ${isVisible
              ? 'opacity-100 scale-100 translate-y-0'
              : 'opacity-0 scale-95 translate-y-6'}
          `}
        >
          {/* decorations */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)`,
              backgroundSize: '28px 28px',
            }}
          />

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-10 p-10 lg:p-14">
            
            {/* left content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/20 text-white rounded-full px-4 py-1.5 text-xs font-semibold mb-4">
                <ScanHeart size={15} className="text-white/90" />
                Ready to check your risk?
              </div>
              <h2 className="text-3xl font-bold text-white mb-3 leading-tight">
                No more delay,{' '}
                <span className="text-cyan-300">take the first step today</span>
              </h2>
              
              <p className="text-blue-100 text-base max-w-xl leading-relaxed mb-6">
                Take the first step toward understanding your diabetes risk. Upload a tongue photo and get your results in seconds.
              </p>

              {/* benefits list */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {benefits?.map((benefit) => (
                  <div key={benefit} className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-cyan-300 flex-shrink-0" />
                    <span className="text-blue-100 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* right content */}
            <div className="flex flex-col items-center gap-4 flex-shrink-0">
              <Link
                href="/upload-page"
                className="group flex items-center gap-3 bg-white text-blue-600 font-bold rounded-full px-10 py-5 text-base shadow-strong hover:shadow-[0_8px_30px_rgba(0,0,0,0.2)] hover:scale-105 transition-all duration-200 active:scale-95 whitespace-nowrap"
              >
                <Scan size={20} />
                Start Free Scan
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <p className="text-blue-200 text-xs text-center">
                Takes less than 2 minutes · No registration
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}